import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchUnreadContributionNotifications,
  markNotificationsRead,
  type AppNotification,
} from '../services/api';
import { contributionIdFromActionUrl } from '../utils/contributionNotification';

const POLL_MS = 45_000;

export function useContributionNotifications(
  isAuthenticated: boolean,
  onOpenContribution?: (contributionId: string) => void,
) {
  const [toast, setToast] = useState<AppNotification | null>(null);
  const shownIdsRef = useRef<Set<number>>(new Set());

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !localStorage.getItem('auth_token')) return;
    try {
      const items = await fetchUnreadContributionNotifications();
      const next = items.find((n) => !shownIdsRef.current.has(n.id));
      if (next) {
        shownIdsRef.current.add(next.id);
        setToast(next);
      }
    } catch {
      /* ignore — token may be invalid during rehydration */
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setToast(null);
      return;
    }
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [isAuthenticated, refresh]);

  const dismissToast = useCallback(async () => {
    if (!toast) return;
    const id = toast.id;
    setToast(null);
    try {
      await markNotificationsRead([id]);
    } catch {
      /* best effort */
    }
  }, [toast]);

  const openToast = useCallback(() => {
    if (!toast) return;
    const contribId = contributionIdFromActionUrl(toast.action_url);
    if (contribId) {
      onOpenContribution?.(contribId);
    } else if (toast.action_url) {
      window.location.href = toast.action_url;
    }
    void dismissToast();
  }, [toast, dismissToast, onOpenContribution]);

  return { toast, dismissToast, openToast };
}
